/**
 * The user library — §4.1, §13.1.
 *
 * Write-through to IndexedDB, MIRRORED IN MEMORY so components render synchronously.
 * Owner of: notes, bookmarks, page state, SRS cards, programmes.
 */
import { create } from 'zustand'
import {
  advanceCursor,
  createProgram,
  dayStamp,
  grade as gradeCard,
  newCard,
  settledPages,
  setVolume,
  type Bookmark,
  type CreateProgramInput,
  type Meta,
  type Note,
  type PageState,
  type Program,
  type Quality,
  type SrsCard,
  type VolumeUnit,
} from '@mushaf/corpus'
import { putKv, put, readSnapshot, remove, replaceAll, type LibrarySnapshot } from '../lib/db'

interface LibraryState {
  hydrated: boolean
  notes: Record<string, Note>
  bookmarks: Record<string, Bookmark>
  srs: Record<number, SrsCard>
  pages: Record<number, PageState>
  programs: Record<string, Program>
  activeProgram: string | null

  hydrate: () => Promise<void>
  replaceFromSnapshot: (snapshot: LibrarySnapshot) => Promise<void>
  snapshot: () => LibrarySnapshot

  setNote: (verseKey: string, text: string) => Promise<void>
  toggleBookmark: (verseKey: string) => Promise<void>
  markRead: (page: number, now: number) => Promise<void>
  setMemorised: (page: number, memorised: boolean, now: number) => Promise<void>
  gradePage: (page: number, quality: Quality, now: number) => Promise<void>

  startProgram: (input: CreateProgramInput) => Promise<Program>
  saveProgram: (program: Program) => Promise<void>
  deleteProgram: (id: string) => Promise<void>
  setActiveProgram: (id: string | null) => Promise<void>
  editVolume: (
    id: string,
    volume: { unit: VolumeUnit; amount: number; review: number },
    now: number,
  ) => Promise<void>
  completeToday: (id: string, lines: number, index: Meta, now: number) => Promise<void>
}

const byKey = <T, K extends string | number>(list: T[], key: (t: T) => K): Record<K, T> =>
  Object.fromEntries(list.map((t) => [key(t), t])) as Record<K, T>

export const useLibrary = create<LibraryState>()((set, get) => ({
  hydrated: false,
  notes: {},
  bookmarks: {},
  srs: {},
  pages: {},
  programs: {},
  activeProgram: null,

  hydrate: async () => {
    const s = await readSnapshot()
    set({
      hydrated: true,
      notes: byKey(s.notes, (n) => n.verseKey),
      bookmarks: byKey(s.bookmarks, (b) => b.verseKey),
      srs: byKey(s.srs, (c) => c.page),
      pages: byKey(s.pages, (p) => p.page),
      programs: byKey(s.programs, (p) => p.id),
      activeProgram: s.activeProgram,
    })
  },

  replaceFromSnapshot: async (snapshot) => {
    await replaceAll(snapshot)
    await get().hydrate()
  },

  snapshot: () => {
    const s = get()
    return {
      notes: Object.values(s.notes),
      bookmarks: Object.values(s.bookmarks),
      srs: Object.values(s.srs),
      pages: Object.values(s.pages),
      programs: Object.values(s.programs),
      activeProgram: s.activeProgram,
    }
  },

  setNote: async (verseKey, text) => {
    const trimmed = text.trim()
    // AN EMPTIED NOTE IS A DELETED NOTE, or the list fills with blank entries the
    // reader cannot see or remove (§8).
    if (!trimmed) {
      const notes = { ...get().notes }
      delete notes[verseKey]
      set({ notes })
      await remove('notes', verseKey)
      return
    }
    const note: Note = { verseKey, text: trimmed, updatedAt: Date.now() }
    set({ notes: { ...get().notes, [verseKey]: note } })
    await put('notes', note)
  },

  toggleBookmark: async (verseKey) => {
    const bookmarks = { ...get().bookmarks }
    if (bookmarks[verseKey]) {
      delete bookmarks[verseKey]
      set({ bookmarks })
      await remove('bookmarks', verseKey)
      return
    }
    const bookmark: Bookmark = { verseKey, createdAt: Date.now() }
    set({ bookmarks: { ...bookmarks, [verseKey]: bookmark } })
    await put('bookmarks', bookmark)
  },

  markRead: async (page, now) => {
    const today = dayStamp(now)
    const existing = get().pages[page]
    // Must NOT rewrite readOn if the page was already read today — it makes no
    // difference to the streak and churns the store on every page turn (§13.1).
    if (existing?.read && existing.readOn === today) return
    const next: PageState = {
      page,
      read: true,
      memorised: existing?.memorised ?? false,
      readOn: today,
    }
    set({ pages: { ...get().pages, [page]: next } })
    await put('pages', next)
  },

  setMemorised: async (page, memorised, now) => {
    const existing = get().pages[page]
    const next: PageState = {
      page,
      read: existing?.read ?? true,
      memorised,
      readOn: existing?.readOn ?? dayStamp(now),
    }
    set({ pages: { ...get().pages, [page]: next } })
    await put('pages', next)

    // MARKING A PAGE MEMORISED IS WHAT ENROLS IT IN THE MURĀJAʿAH QUEUE (§10.1).
    // Un-marking deletes the card.
    const srs = { ...get().srs }
    if (memorised) {
      if (!srs[page]) {
        const card = newCard(page, now)
        srs[page] = card
        set({ srs })
        await put('srs', card)
      }
    } else if (srs[page]) {
      delete srs[page]
      set({ srs })
      await remove('srs', page)
    }
  },

  gradePage: async (page, quality, now) => {
    const existing = get().srs[page] ?? newCard(page, now)
    const next = gradeCard(existing, quality, now)
    set({ srs: { ...get().srs, [page]: next } })
    await put('srs', next)
  },

  startProgram: async (input) => {
    const program = createProgram(input)
    // ONE ACTIVE PROGRAMME AT A TIME. Activating a new one pauses the current one and
    // does NOT delete its progress.
    const programs = { ...get().programs }
    const previous = get().activeProgram ? programs[get().activeProgram!] : undefined
    if (previous && previous.state === 'active') {
      const paused = { ...previous, state: 'paused' as const }
      programs[paused.id] = paused
      await put('programs', paused)
    }
    programs[program.id] = program
    set({ programs, activeProgram: program.id })
    await put('programs', program)
    await putKv('activeProgram', program.id)
    return program
  },

  saveProgram: async (program) => {
    set({ programs: { ...get().programs, [program.id]: program } })
    await put('programs', program)
  },

  deleteProgram: async (id) => {
    const programs = { ...get().programs }
    delete programs[id]
    const activeProgram = get().activeProgram === id ? null : get().activeProgram
    set({ programs, activeProgram })
    await remove('programs', id)
    if (activeProgram !== get().activeProgram) await putKv('activeProgram', activeProgram)
  },

  setActiveProgram: async (id) => {
    set({ activeProgram: id })
    await putKv('activeProgram', id)
  },

  editVolume: async (id, volume, now) => {
    const program = get().programs[id]
    if (!program) return
    await get().saveProgram(setVolume(program, volume, now))
  },

  /**
   * Capture the cursor, advance it, persist, then settle the pages the cursor has
   * genuinely moved PAST (§11.5).
   *
   * A `murajaah` rotation SETTLES NOTHING — it revisits pages that are already
   * learned.
   */
  completeToday: async (id, lines, index, now) => {
    const program = get().programs[id]
    if (!program) return

    const before = program.cursor
    const advanced = advanceCursor(program, lines, index)
    await get().saveProgram(advanced)

    if (program.kind === 'murajaah') return

    const pages = settledPages(before, advanced.cursor, advanced.state === 'done')
    for (const page of pages) {
      if (program.kind === 'hifz') await get().setMemorised(page, true, now)
      else await get().markRead(page, now)
    }
  },
}))

export const selectMemorisedPages = (s: LibraryState): number[] =>
  Object.values(s.pages)
    .filter((p) => p.memorised)
    .map((p) => p.page)
    .sort((a, b) => a - b)

export const selectActiveProgram = (s: LibraryState): Program | null =>
  s.activeProgram ? (s.programs[s.activeProgram] ?? null) : null
